/**
 * Pins a hero section's oversized Logo block in the viewport while the
 * visitor scrolls through the hero's extra scroll height (see hero.liquid's
 * `.hero--pin-logo` / `--hero-pin-scroll-vh`), continuously scaling and
 * translating that single logo element from its large hero position down to
 * wherever the real header logo (`.header-logo`) currently sits. It is the
 * same element throughout — nothing is cloned, and no separate header logo
 * is created. Once scroll progress reaches 1 the element switches from
 * `position: absolute` (tracking the sticky hero wrapper) to
 * `position: fixed` at the header logo's rect, so it stays visually merged
 * with the header as the page keeps scrolling past the hero. The theme's
 * own `.header-logo` is kept hidden for the lifetime of this feature — the
 * hero's logo block *is* the header logo from that point on.
 *
 * Horizon's squeeze layout scrolls inside `.page-wrapper` on desktop
 * (≥990px), not `window` — this uses the theme's own scroll-container
 * helpers (the same ones header.js uses) so measurement and the scroll
 * listener always target whichever element is actually scrolling.
 *
 * Skipped (no pin, no animation) when prefers-reduced-motion is set, or
 * when the hero has no Logo block / the header has no logo — the native
 * static layout (small header logo, static hero content) is used instead.
 */
import { getScrollTop, getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function lerp(start, end, t) {
  return start + (end - start) * t;
}

function init() {
  var hero = document.querySelector('.hero[data-cm-logo-shrink="true"]');
  if (!hero) return;

  var pinWrapper = hero.querySelector('.hero__container');
  var heroLogo = hero.querySelector('.logo-block');
  var headerLogo = document.querySelector('.header-logo');
  if (!pinWrapper || !heroLogo || !headerLogo) return;

  // Kept hidden for the entire lifetime of this feature: the hero's own logo
  // block is the single element that ends up sitting in this slot.
  //
  // Flagged on <body>, NOT as an inline property on .header-logo. The header
  // re-renders when its sticky/scroll-up state changes, and a re-render does not
  // preserve inline styles our script added — .header-logo would snap back to
  // opacity 1 and paint UNDERNEATH the docked hero logo, which is the doubled
  // wordmark on scroll-up. <body> is outside the header, so the morph can't
  // touch it and the rule can't be undone.
  document.body.classList.add('cm-logo-pinned');

  // cm-hero-logo-shrink.css targets this element via this attribute rather
  // than a `.hero ... .logo-block` descendant selector, since dock()
  // reparents the element to <body> — its styling needs to stay attached
  // regardless of which parent it currently sits under.
  heroLogo.setAttribute('data-cm-logo-pin', '');

  // `.hero__container` sets `z-index: var(--layer-base)` (needed so the
  // hero layers correctly against other page sections) — that gives it its
  // own stacking context. `position: fixed` only escapes normal LAYOUT, not
  // an ancestor's stacking context, so left alone the docked logo would
  // render behind the header's own opaque background no matter how high a
  // z-index it's given itself. The docked logo therefore has to be reparented
  // out of the hero.
  //
  // It goes into the header rather than <body>: the header takes itself away
  // once you scroll down past the hero (sticky="scroll-up", via a transform),
  // and a logo parked on <body> does not follow it — it just hangs over the
  // page content forever. Living inside the header, it inherits that transform
  // for free. The header is position:fixed, so it's also a containing block for
  // an absolutely positioned child, and overflow:visible so it won't clip.
  var headerHost = headerLogo.closest('header-component');
  if (headerHost && getComputedStyle(headerHost).position === 'static') headerHost = null;

  var originalParent = heroLogo.parentNode;
  var originalNextSibling = heroLogo.nextSibling;

  function restoreOriginalPosition() {
    if (heroLogo.parentNode === originalParent) return;
    if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
      originalParent.insertBefore(heroLogo, originalNextSibling);
    } else {
      originalParent.appendChild(heroLogo);
    }
  }

  function resetLogoStyles() {
    heroLogo.style.position = '';
    heroLogo.style.top = '';
    heroLogo.style.left = '';
    heroLogo.style.width = '';
    heroLogo.style.transform = '';
    heroLogo.style.transformOrigin = '';
    // measure() resets `docked` to false directly rather than via undock(), so
    // the class has to be cleared here too — otherwise a transient early dock
    // (before layout settles, when the hero's height can briefly read as 0 and
    // force progress to 1) leaves .is-docked stuck on, and the
    // `:not(.is-docked)` scrub rule never matches again.
    heroLogo.classList.remove('is-docked');
  }

  if (reduceMotion) {
    var destRect = headerLogo.getBoundingClientRect();
    var srcRect = heroLogo.getBoundingClientRect();
    var rmImgEl = heroLogo.querySelector('img');
    var rmImg = rmImgEl ? rmImgEl.getBoundingClientRect() : srcRect;
    if (srcRect.width > 0 && srcRect.height > 0 && rmImg.width > 0) {
      var reducedScale = Math.min(destRect.width / rmImg.width, destRect.height / rmImg.height);
      // Centre the scaled wordmark image within the header slot, then back out
      // to the block's top-left (the element being positioned) via the image's
      // offset inside the block.
      var rmImgLeft = destRect.left + destRect.width / 2 - (rmImg.width * reducedScale) / 2;
      var rmImgTop = destRect.top + destRect.height / 2 - (rmImg.height * reducedScale) / 2;
      var rmLeft = rmImgLeft - (rmImg.left - srcRect.left) * reducedScale;
      var rmTop = rmImgTop - (rmImg.top - srcRect.top) * reducedScale;

      // Same reasoning as dock(): park it in the header so it travels with the
      // header's show/hide rather than floating over the page.
      var rmHost = headerLogo.closest('header-component');
      if (rmHost && getComputedStyle(rmHost).position !== 'static') {
        var rmHostRect = rmHost.getBoundingClientRect();
        rmHost.appendChild(heroLogo);
        heroLogo.style.position = 'absolute';
        heroLogo.style.left = rmLeft - rmHostRect.left + 'px';
        heroLogo.style.top = rmTop - rmHostRect.top + 'px';
      } else {
        document.body.appendChild(heroLogo);
        heroLogo.style.position = 'fixed';
        heroLogo.style.left = rmLeft + 'px';
        heroLogo.style.top = rmTop + 'px';
      }

      heroLogo.style.width = srcRect.width + 'px';
      heroLogo.style.transformOrigin = 'top left';
      heroLogo.style.transform = 'scale(' + reducedScale + ')';
    }
    return;
  }

  var docked = false;
  var ticking = false;
  var progress = -1;
  var sectionTop = 0;
  var range = 0;
  var srcTop = 0;
  var srcLeft = 0;
  var srcWidth = 0;
  var srcHeight = 0;
  // The visible wordmark image inside the logo block, and its offset within
  // that block. The block is taller than the image (it carries bottom padding),
  // so docking must centre the IMAGE — not the padded block — in the header
  // slot, or the wordmark lands too high and misaligns with the header icons.
  var imgW = 0;
  var imgH = 0;
  var imgOffsetTop = 0;
  var imgOffsetLeft = 0;
  var parentRect = null;
  var scrollTarget = null;

  // The logo→header delta, cached rather than read every frame — that keeps a
  // getBoundingClientRect() (a forced synchronous layout) off every scroll
  // frame.
  //
  // The delta is only invariant once BOTH ends of it have settled, and at load
  // neither has:
  //   - the header collapses into its compact scrolled state, lifting its logo
  //     slot by the announcement bar's height (measured: centre y 55 -> 28);
  //   - the hero's sticky container hasn't pinned yet, so the logo's own
  //     untransformed origin is still moving too.
  // measure() runs at scroll ~0, so the delta it computes is wrong by ~27px,
  // and that disagreement with dock()'s live read *is* the snap at the dock
  // boundary.
  //
  // The header collapses on a CSS transition — i.e. over TIME, not over scroll
  // offset — so no scroll threshold can be trusted to mean "it has finished
  // moving". Instead trackDest() simply re-reads until the answer stops
  // changing, then never reads again.
  var deltaX = 0;
  var deltaY = 0;
  var dockedScale = 1;
  var deltaSettled = false;
  var settleFrames = 0;
  var lastDeltaY = NaN;

  // When the browser supports scroll-driven animations the scrub can run as a
  // real CSS animation on the compositor, tracking the finger with zero lag.
  // This flag only decides whether to publish the custom properties that drive
  // it — it is NOT a promise that the timeline works.
  //
  // Support is necessary but not sufficient: a scroll timeline whose source
  // turns out not to be scrollable resolves but stays permanently inactive
  // (currentTime null, animating nothing), and it can go inactive AFTER a check
  // says it's live. So nothing detects it. update() writes the JS transform
  // unconditionally instead; an active timeline outranks an inline style, so the
  // compositor wins when it works and this is already in place when it doesn't.
  var cssScrub = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('animation-timeline', 'scroll()');

  // Scales to fit the header logo's box on both axes (like object-fit:
  // contain) rather than by width alone — a plain width-ratio scale looks
  // right for an image-based logo (width and height already share the same
  // aspect ratio as the header's), but distorts badly for the shop-name
  // text fallback, whose hero-sized box is far wider relative to its height
  // than the compact header slot is.
  function dockScale(destRect) {
    if (imgW <= 0 || imgH <= 0) return 1;
    return Math.min(destRect.width / imgW, destRect.height / imgH);
  }

  // Block top-left (viewport coords) that lands the scaled wordmark IMAGE
  // centred inside the header's logo slot — accounts for the image's own
  // offset within the bottom-padded block so the visible wordmark, not the
  // padded box, aligns with the header's hamburger/search/cart icons.
  function dockedPosition(destRect, s) {
    var slotCx = destRect.left + destRect.width / 2;
    var slotCy = destRect.top + destRect.height / 2;
    var imgLeft = slotCx - (imgW * s) / 2;
    var imgTop = slotCy - (imgH * s) / 2;
    return {
      left: imgLeft - imgOffsetLeft * s,
      top: imgTop - imgOffsetTop * s,
    };
  }

  // Recomputes the logo→header delta from both ends' CURRENT positions and
  // hands the result to whichever scrub is driving. `parentRect` is re-read
  // here rather than reused: the logo is absolutely positioned inside the
  // hero's sticky container, so its own untransformed origin moves with scroll
  // until that container pins — using the cached (unpinned) rect would bake the
  // same error back in from the other side.
  function computeDelta() {
    var offsetParent = heroLogo.offsetParent || pinWrapper;
    parentRect = offsetParent.getBoundingClientRect();

    var destRect = headerLogo.getBoundingClientRect();
    dockedScale = dockScale(destRect);
    var endPos = dockedPosition(destRect, dockedScale);
    deltaX = endPos.left - (parentRect.left + srcLeft);
    deltaY = endPos.top - (parentRect.top + srcTop);

    // Only touch the custom properties when they actually moved: trackDest()
    // calls this every frame until the header settles, and writing inline
    // styles unconditionally would dirty style on each of those frames.
    if (cssScrub && deltaY !== lastDeltaY) {
      heroLogo.style.setProperty('--cm-logo-dx', deltaX + 'px');
      heroLogo.style.setProperty('--cm-logo-dy', deltaY + 'px');
      heroLogo.style.setProperty('--cm-logo-scale', dockedScale);
    }

    return destRect;
  }

  // Re-reads the dock target until it holds still for a few consecutive frames,
  // then latches. Gated on p > 0.5 as well so a pause in scrolling mid-collapse
  // can't latch a half-settled value: the header is done by ~100px, long before
  // the midpoint of the scrub, and every read here happens while the logo is
  // still near full size, where a correction is imperceptible.
  function trackDest(p) {
    computeDelta();
    if (deltaY === lastDeltaY) {
      if (++settleFrames >= 3 && p > 0.5) deltaSettled = true;
    } else {
      settleFrames = 0;
      lastDeltaY = deltaY;
    }
    apply(p);
  }

  function measure() {
    // If a resize/font-load remeasure lands while docked, the element is
    // currently living in <body> — offsetParent/rect reads below need its
    // natural hero-relative position, not wherever dock() last moved it to.
    restoreOriginalPosition();
    resetLogoStyles();

    // offsetParent is whatever CSS containing block .logo-block naturally
    // resolves to (in practice .hero__content-wrapper) — reading it live
    // rather than assuming a fixed ancestor keeps this correct even if the
    // theme's hero markup nesting changes.
    var offsetParent = heroLogo.offsetParent || pinWrapper;
    var srcRect = heroLogo.getBoundingClientRect();
    parentRect = offsetParent.getBoundingClientRect();

    srcTop = srcRect.top - parentRect.top;
    srcLeft = srcRect.left - parentRect.left;
    srcWidth = srcRect.width;
    srcHeight = srcRect.height;

    // Capture the visible wordmark image's size and its offset within the
    // (bottom-padded) block, so docking centres the image in the header slot.
    var imgEl = heroLogo.querySelector('img');
    if (imgEl) {
      var imgRect = imgEl.getBoundingClientRect();
      imgW = imgRect.width;
      imgH = imgRect.height;
      imgOffsetTop = imgRect.top - srcRect.top;
      imgOffsetLeft = imgRect.left - srcRect.left;
    } else {
      imgW = srcWidth;
      imgH = srcHeight;
      imgOffsetTop = 0;
      imgOffsetLeft = 0;
    }

    if (srcWidth > 0) {
      heroLogo.style.position = 'absolute';
      heroLogo.style.top = srcTop + 'px';
      heroLogo.style.left = srcLeft + 'px';
      heroLogo.style.width = srcWidth + 'px';
      heroLogo.style.transformOrigin = 'top left';
    }

    var container = getScrollContainer();
    var containerRect = container.getBoundingClientRect();
    var heroRect = hero.getBoundingClientRect();
    // Transparent-header heroes get pulled up under the header via a
    // negative margin (see base.css), which can make the sticky wrapper
    // already engaged (rect.top: 0) at real scrollTop 0 — the geometric
    // offset comes out negative. Since scrollTop can never be negative,
    // clamping to 0 correctly starts progress at the hero's resting state
    // instead of already-partway through the shrink on load.
    sectionTop = Math.max(0, heroRect.top - containerRect.top + container.scrollTop);
    range = hero.offsetHeight - container.clientHeight;

    // Provisional delta from the current (still unsettled) positions — good
    // enough to render the resting state; trackDest() converges it on the real
    // one as soon as scrolling starts.
    lastDeltaY = NaN;
    deltaSettled = false;
    settleFrames = 0;
    computeDelta();

    if (cssScrub) {
      // Hand the scrub to CSS: keyframes interpolate this transform across
      // the hero's pin range on a scroll timeline (cm-hero-logo-shrink.css).
      // The range must START at sectionTop, not 0 — update() measures progress
      // as (scrollTop - sectionTop) / range, and a CSS range anchored at 0
      // would finish the scrub sectionTop pixels early, leaving the logo
      // stranded (and drifting off-screen with the un-pinning hero) until JS
      // finally docked it.
      heroLogo.style.setProperty('--cm-logo-start', sectionTop + 'px');
      heroLogo.style.setProperty('--cm-logo-range', Math.max(0, range) + 'px');
      heroLogo.classList.add('cm-logo-scrub-css');
    }

    docked = false;
    progress = -1;
    update();
  }

  // An active scroll timeline reports a currentTime (0 at the top of its range);
  // an inactive one reports null forever. Checked after a frame so styles have
  // settled. Only ever demotes — once we're on the JS scrub we stay there.

  function dock() {
    if (docked) return;
    docked = true;
    if (srcWidth <= 0) return;

    // One layout read, once, at the moment of docking — not per frame. Read
    // live rather than reusing a cached figure: this is the ground truth for
    // where the logo comes to rest, and by p=1 the header has long finished
    // collapsing. trackDest() has already converged the scrub on this same
    // spot, so the handoff is seamless.
    var destRect = headerLogo.getBoundingClientRect();
    var s = dockScale(destRect);
    var pos = dockedPosition(destRect, s);

    // Stop the CSS scrub so it can't fight the docked transform (animations
    // outrank inline styles in the cascade).
    heroLogo.classList.add('is-docked');

    if (headerHost) {
      // Offsets are taken from both post-transform rects, so they describe the
      // logo's place WITHIN the header and stay correct whether the header is
      // currently shown, hidden, or mid-transition.
      var hostRect = headerHost.getBoundingClientRect();
      headerHost.appendChild(heroLogo);
      heroLogo.style.position = 'absolute';
      heroLogo.style.top = pos.top - hostRect.top + 'px';
      heroLogo.style.left = pos.left - hostRect.left + 'px';
    } else {
      document.body.appendChild(heroLogo);
      heroLogo.style.position = 'fixed';
      heroLogo.style.top = pos.top + 'px';
      heroLogo.style.left = pos.left + 'px';
    }

    heroLogo.style.width = srcWidth + 'px';
    heroLogo.style.transform = 'scale(' + s + ')';
  }

  function undock() {
    if (!docked) return;
    docked = false;

    restoreOriginalPosition();
    heroLogo.style.position = 'absolute';
    heroLogo.style.top = srcTop + 'px';
    heroLogo.style.left = srcLeft + 'px';
    // Hand the scrub back to the CSS animation.
    heroLogo.classList.remove('is-docked');
  }

  // JS fallback scrub (browsers without scroll-driven animations). Uses the
  // delta cached by measure(), so this is a pure transform write — no layout
  // read — which keeps even the fallback path off the thrash path.
  function apply(p) {
    if (srcWidth <= 0) return;
    var scale = lerp(1, dockedScale, p);
    heroLogo.style.transform =
      'translate(' + deltaX * p + 'px, ' + deltaY * p + 'px) scale(' + scale + ')';
  }

  // Dock/undock toggle position between `fixed` and `absolute`, one of the
  // most expensive style changes a browser can make (it restructures the
  // element's containing-block chain and stacking context), and each also
  // forces a fresh `getBoundingClientRect()` read. Right at the p===1
  // boundary, momentum-scroll can leave `scrollTop` oscillating by a
  // sub-pixel amount between frames, which without a dead zone would flip
  // dock()/undock() every single frame for as long as the scroll is
  // settling. Docking still engages at the exact end of the scrub range;
  // undocking only releases once scroll has backed off far enough (2% of
  // the range) that it's a deliberate reverse-scroll, not jitter.
  var UNDOCK_THRESHOLD = 0.98;

  function update() {
    ticking = false;

    var scrollTop = getScrollTop();
    var p = range > 0 ? (scrollTop - sectionTop) / range : 1;
    p = clamp(p, 0, 1);

    // Runs before the `p === progress` early-out: the header can still be
    // collapsing while p sits at 0, and that is exactly when the target needs
    // correcting.
    if (!deltaSettled) trackDest(p);

    if (p === progress) return;
    progress = p;

    if (!docked && progress >= 1) {
      dock();
      return;
    }

    if (docked) {
      if (progress < UNDOCK_THRESHOLD) {
        undock();
        apply(progress);
      }
      return;
    }

    // Always write the transform, even when the CSS scrub should be driving.
    // An ACTIVE scroll timeline outranks an inline style, so this costs nothing
    // when the compositor path works — and when that path silently dies (an
    // inactive timeline reports no currentTime and animates nothing) this is
    // already in place, so the logo keeps tracking instead of freezing at full
    // size. Cheaper and more reliable than detecting which path is live.
    apply(progress);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function bindScrollTarget() {
    if (scrollTarget) {
      scrollTarget.removeEventListener('scroll', onScroll);
    }
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  }

  // If the tab is backgrounded mid-scroll, its pending rAF callback is
  // suspended by the browser and never resolves, permanently stranding
  // `ticking` at true and silently freezing all future updates. Reset it
  // on return so scrolling resumes working immediately.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      ticking = false;
      update();
    }
  });

  var resizeTimeout;
  function onResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(measure, 100);
  }

  bindScrollTarget();
  measure();
  window.addEventListener('resize', onResize);
  window.addEventListener('load', measure);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(measure);
  }
  // Re-bind when crossing the 990px squeeze breakpoint, since the scroll
  // container itself changes between .page-wrapper and document.
  scrollContainerMediaQuery.addEventListener('change', function () {
    bindScrollTarget();
    measure();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
